import React, { useState, useEffect, useRef, useCallback, useContext, useMemo } from 'react';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import {
  PageHeader,
  StandardColumn,
  StandardDataTable,
  useColumnsState,
} from '../../../components/StandardPage';
import { ViewModeSelector, SortBySelector, SortOption } from '../Pipelines/PipelineControls';
import { OverflowCell } from '../../../components/OverflowCell';
import AttachmentApiService, { GalleryFile, AttachmentUsage } from '../../../services/AttachmentApiService';
import ImageLightbox from '../../../components/ImageViewer';
import { FeedbackContext } from '../../../contexts/FeedbackContext';
import { useTenant } from '../../../contexts/TenantContext';
import LoadMoreButton from '../../../components/LoadMoreButton';
import LoadingComponent from '../../../components/LoadingComponent';
import { TypeMainOutlet } from '..';
import { useSearch } from '../../../contexts/SearchContext';
import {
  UploadCloud,
  File,
  FileText,
  Image as ImageIcon,
  Table as TableIcon,
  Loader2,
  ZoomIn,
  Layers,
  Eye,
  Edit2,
  Info,
  Download,
  Trash2,
  X,
  FolderOpen,
  List,
} from 'lucide-react';
import { openSignedAttachment, getSignedAttachmentUrl } from '../../../utils/openSignedAttachment';
import { downloadRemoteFile } from '../../../utils/downloadRemoteFile';
import { ensureFileExtension } from '../../../components/AttachmentGalleryModal';
import * as S from './style';

const ITEMS_PER_PAGE = 50;
const attachmentsScreenKey = 'attachments_table';

const attachmentTypeOptions = [
  { value: 'all', label: 'Todos os tipos', icon: <List size={16} /> },
  { value: 'image', label: 'Imagens', icon: <ImageIcon size={16} /> },
  { value: 'pdf', label: 'PDFs', icon: <FileText size={16} /> },
  { value: 'document', label: 'Documentos', icon: <File size={16} /> },
  { value: 'spreadsheet', label: 'Planilhas', icon: <TableIcon size={16} /> },
  { value: 'other', label: 'Outros', icon: <FolderOpen size={16} /> },
];

const attachmentUsageOptions = [
  { value: 'all', label: 'Todos os usos', icon: <Layers size={16} /> },
  { value: 'used', label: 'Com uso', icon: <Layers size={16} /> },
  { value: 'unused', label: 'Sem uso', icon: <Layers size={16} /> },
];

const attachmentSortOptions: SortOption[] = [
  { value: 'recent', label: 'Mais recentes' },
  { value: 'oldest', label: 'Mais antigos' },
  { value: 'name_asc', label: 'Nome (A-Z)' },
  { value: 'name_desc', label: 'Nome (Z-A)' },
  { value: 'size_desc', label: 'Maior tamanho' },
  { value: 'size_asc', label: 'Menor tamanho' },
];

export const AttachmentsPage: React.FC = () => {
  const navigate = useNavigate();
  const outletContext = useOutletContext<TypeMainOutlet | undefined>();
  const { search: contextSearch } = useSearch();
  const { setFeedback } = useContext(FeedbackContext);
  const { isFashionTenant } = useTenant();

  const [viewMode, setViewMode] = useState<'board' | 'table'>('board');
  const [files, setFiles] = useState<GalleryFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [failedImages, setFailedImages] = useState<Record<number, boolean>>({});

  const globalSearch = typeof outletContext?.search === 'string' ? outletContext.search : (contextSearch || '');
  const [debouncedSearch, setDebouncedSearch] = useState(globalSearch);
  const [typeFilter, setTypeFilter] = useState<'all' | 'image' | 'pdf' | 'document' | 'spreadsheet' | 'other'>('all');
  const [usageFilter, setUsageFilter] = useState<'all' | 'used' | 'unused'>('all');
  const [sortBy, setSortBy] = useState<string>('recent');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [activeMenuFileId, setActiveMenuFileId] = useState<number | null>(null);

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const [usageFile, setUsageFile] = useState<GalleryFile | null>(null);
  const [usageList, setUsageList] = useState<AttachmentUsage[]>([]);
  const [loadingUsage, setLoadingUsage] = useState(false);

  const [editingFile, setEditingFile] = useState<GalleryFile | null>(null);
  const [editName, setEditName] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const [deletingFile, setDeletingFile] = useState<GalleryFile | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [fileToUpload, setFileToUpload] = useState<File | null>(null);
  const [customTitle, setCustomTitle] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const urlAttachmentId = searchParams.get('attachmentId') || searchParams.get('usageId');
  const openingUsageIdRef = useRef<number | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch((prev) => {
        if (prev !== globalSearch) {
          setPage(1);
          return globalSearch;
        }
        return prev;
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [globalSearch]);

  const fetchGallery = useCallback(
    async (targetPage: number, append: boolean = false) => {
      try {
        if (append) {
          setLoadingMore(true);
        } else {
          setLoading(true);
        }

        const params: any = {
          page: targetPage,
          limit: ITEMS_PER_PAGE,
          sortBy,
        };
        if (debouncedSearch.trim()) {
          params.search = debouncedSearch.trim();
        }
        if (typeFilter && typeFilter !== 'all') {
          params.type = typeFilter;
        }
        if (usageFilter && usageFilter !== 'all') {
          params.usage = usageFilter;
        }

        const res = await AttachmentApiService.getGallery(params);
        if (res) {
          const rawFiles = res.data || [];

          if (append) {
            setFiles((prev) => {
              const existingIds = new Set(prev.map((f) => f.id));
              const freshFiles = rawFiles.filter((f) => !existingIds.has(f.id));
              return [...prev, ...freshFiles];
            });
          } else {
            setFiles(rawFiles);
          }

          setPage(targetPage);
          setTotalPages(res.totalPages || 1);
          setTotalItems(res.total || 0);
        }
      } catch (error) {
        console.error(error);
        setFeedback({ isOpen: true, message: 'Erro ao carregar anexos', statusOk: false });
      } finally {
        if (append) {
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
      }
    },
    [debouncedSearch, typeFilter, usageFilter, sortBy, setFeedback]
  );

  useEffect(() => {
    fetchGallery(1, false);
  }, [fetchGallery]);

  const handleLoadMore = () => {
    if (loading || loadingMore || files.length >= totalItems || page >= totalPages) return;
    fetchGallery(page + 1, true);
  };

  useEffect(() => {
    const handleWindowClick = () => setActiveMenuFileId(null);
    window.addEventListener('click', handleWindowClick);
    return () => window.removeEventListener('click', handleWindowClick);
  }, []);

  const formatSize = (bytes?: number | null): string => {
    if (bytes === undefined || bytes === null) return '-';
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  const formatDate = (isoString?: string): string => {
    if (!isoString) return '';
    try {
      const date = new Date(isoString);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return '';
    }
  };

  const getFileBadgeInfo = (mimeType?: string | null, fileName?: string | null) => {
    const rawName = (fileName || '').trim();
    const ext = rawName ? rawName.split('?')[0].split('#')[0].split('.').pop()?.toLowerCase() || '' : '';
    const safeMime = (mimeType || '').toLowerCase();
    const imageExtensions = ['jpg', 'jpeg', 'jfif', 'pjpeg', 'pjp', 'png', 'webp', 'gif', 'svg', 'bmp', 'avif', 'ico', 'tif', 'tiff'];

    if (safeMime.startsWith('image/') || imageExtensions.includes(ext)) {
      return { type: 'image', icon: <ImageIcon size={26} />, label: 'Imagem' };
    }
    if (safeMime.includes('pdf') || ext === 'pdf') {
      return { type: 'pdf', icon: <FileText size={26} />, label: 'PDF' };
    }
    if (
      safeMime.includes('sheet') ||
      safeMime.includes('excel') ||
      safeMime.includes('csv') ||
      ['xlsx', 'xls', 'csv', 'ods'].includes(ext)
    ) {
      return { type: 'sheet', icon: <TableIcon size={26} />, label: 'Planilha' };
    }
    if (
      safeMime.includes('word') ||
      safeMime.includes('officedocument') ||
      ['doc', 'docx', 'odt', 'rtf', 'txt'].includes(ext)
    ) {
      return { type: 'doc', icon: <FileText size={26} />, label: 'Documento' };
    }
    return { type: 'other', icon: <File size={26} />, label: 'Arquivo' };
  };

  const imageFiles = useMemo(() => {
    return files.filter((f) => {
      const badgeInfo = getFileBadgeInfo(f.mimeType, f.fileName || f.originalName || f.title || f.fileUrl);
      return badgeInfo.type === 'image' && !failedImages[f.id];
    });
  }, [files, failedImages]);

  const lightboxSlides = useMemo(() => {
    return imageFiles.map((f) => ({
      src: f.signedUrl || f.fileUrl,
      title: f.title || f.originalName,
    }));
  }, [imageFiles]);

  const imageFilesRef = useRef<GalleryFile[]>(imageFiles);
  imageFilesRef.current = imageFiles;

  const handleOpenLightbox = useCallback((fileId: number) => {
    const currentImages = imageFilesRef.current;
    const idx = currentImages.findIndex((f) => f.id === fileId);
    if (idx !== -1) {
      setLightboxIndex(idx);
      setLightboxOpen(true);
    }
  }, []);

  const handleViewFile = useCallback((file: GalleryFile) => {
    setActiveMenuFileId(null);
    const badgeInfo = getFileBadgeInfo(file.mimeType, file.fileName || file.originalName || file.title || file.fileUrl);
    if (badgeInfo.type === 'image' && !failedImages[file.id]) {
      handleOpenLightbox(file.id);
    } else {
      openSignedAttachment(file.signedUrl || file.fileUrl, file.title || file.originalName);
    }
  }, [failedImages, handleOpenLightbox]);

  const handleDownloadFile = async (file: GalleryFile) => {
    setActiveMenuFileId(null);
    try {
      const safeDownloadName = ensureFileExtension(
        file.title || file.originalName || file.fileName || 'anexo',
        file.fileName || file.originalName,
      );

      let downloadUrl = '';
      try {
        downloadUrl = await AttachmentApiService.getDownloadUrl(file.id);
      } catch (err) {
        void err;
      }
      if (!downloadUrl) {
        downloadUrl = await getSignedAttachmentUrl(
          file.signedUrl || file.fileUrl,
          true,
          safeDownloadName,
        );
      }
      if (!downloadUrl) {
        setFeedback({ isOpen: true, message: 'Não foi possível obter o link de download', statusOk: false });
        return;
      }

      await downloadRemoteFile(downloadUrl, safeDownloadName);
    } catch (err) {
      console.error(err);
      setFeedback({ isOpen: true, message: 'Erro ao realizar download do anexo', statusOk: false });
    }
  };

  const handleOpenEdit = (file: GalleryFile) => {
    setActiveMenuFileId(null);
    setEditingFile(file);
    setEditName(file.title || file.originalName);
  };

  const handleSaveEdit = async () => {
    if (!editingFile || !editName.trim()) {
      setFeedback({ isOpen: true, message: 'O nome do anexo não pode ser vazio', statusOk: false });
      return;
    }
    try {
      setSavingEdit(true);
      const targetOriginalName = ensureFileExtension(
        editName.trim(),
        editingFile.fileName || editingFile.originalName,
      );
      const updated = await AttachmentApiService.update(editingFile.id, {
        title: editName.trim(),
        originalName: targetOriginalName,
      });
      setFiles((prev) =>
        prev.map((f) =>
          f.id === editingFile.id
            ? { ...f, title: updated.title, originalName: updated.originalName }
            : f
        )
      );
      setFeedback({ isOpen: true, message: 'Nome do anexo atualizado com sucesso!', statusOk: true });
      setEditingFile(null);
    } catch (error) {
      console.error(error);
      setFeedback({ isOpen: true, message: 'Erro ao atualizar nome do anexo', statusOk: false });
    } finally {
      setSavingEdit(false);
    }
  };

  const handleOpenUsage = useCallback(
    async (file: GalleryFile) => {
      setActiveMenuFileId(null);
      setUsageFile(file);
      setLoadingUsage(true);
      setUsageList([]);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (next.get('attachmentId') !== String(file.id)) {
          next.set('attachmentId', String(file.id));
          return next;
        }
        return prev;
      });
      try {
        const res = await AttachmentApiService.getUsage(file.id);
        const list = Array.isArray(res)
          ? res
          : Array.isArray((res as any)?.usages)
          ? (res as any).usages
          : Array.isArray((res as any)?.data?.usages)
          ? (res as any).data.usages
          : [];
        setUsageList(list);
      } catch (err) {
        console.error(err);
        setFeedback({ isOpen: true, message: 'Erro ao consultar locais de uso', statusOk: false });
      } finally {
        setLoadingUsage(false);
      }
    },
    [setSearchParams, setFeedback]
  );

  const handleCloseUsage = useCallback(() => {
    setUsageFile(null);
    setUsageList([]);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (next.has('attachmentId') || next.has('usageId')) {
        next.delete('attachmentId');
        next.delete('usageId');
        return next;
      }
      return prev;
    });
  }, [setSearchParams]);

  useEffect(() => {
    if (!urlAttachmentId) {
      openingUsageIdRef.current = null;
      return;
    }
    const targetId = Number(urlAttachmentId);
    if (!targetId || isNaN(targetId)) return;
    if (usageFile?.id === targetId || openingUsageIdRef.current === targetId) return;

    openingUsageIdRef.current = targetId;

    const existing = files.find((f) => f.id === targetId);
    if (existing) {
      handleOpenUsage(existing).finally(() => {
        openingUsageIdRef.current = null;
      });
    } else {
      AttachmentApiService.getById(targetId)
        .then((fetched) => {
          if (fetched) {
            handleOpenUsage(fetched);
          }
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          openingUsageIdRef.current = null;
        });
    }
  }, [urlAttachmentId, files, usageFile?.id, handleOpenUsage]);

  const getModuleBadgeLabel = (item: AttachmentUsage): string => {
    if (item.module === 'STOCK') {
      return isFashionTenant ? 'Tecidos' : 'Estoque';
    }
    if (item.module === 'RESOURCE') {
      return isFashionTenant ? 'Aviamentos' : 'Insumos';
    }
    return item.moduleLabel || item.module;
  };

  const getUsageEditUrl = (item: AttachmentUsage): string => {
    let baseUrl = '';
    if (item.editUrl) {
      if (!isFashionTenant && item.editUrl.includes('/panel/composition-product/edit/')) {
        baseUrl = item.editUrl.replace('/panel/composition-product/edit/', '/panel/product-final/edit/');
      } else if (item.editUrl.includes('/panel/service/edit/')) {
        baseUrl = item.editUrl.replace('/panel/service/edit/?id=', '/panel/production/edit/').replace('/panel/service/edit/', '/panel/production/edit/');
      } else {
        baseUrl = item.editUrl;
      }
    } else if (item.module === 'STOCK') {
      baseUrl = `/panel/stock?stockId=${item.entityId}&step=3`;
    } else if (item.module === 'RESOURCE') {
      baseUrl = `/panel/stock/resource/edit?id=${item.entityId}&step=3`;
    } else if (item.module === 'CERTIFICATE') {
      baseUrl = `/panel/certificates/edit?id=${item.entityId}`;
    } else if (item.module === 'OCCURRENCE') {
      baseUrl = `/panel/certificates/rnc?occurrenceId=${item.entityId}`;
    } else if (item.module === 'PRODUCT') {
      baseUrl = isFashionTenant
        ? `/panel/composition-product/edit/${item.entityId}`
        : `/panel/product-final/edit/${item.entityId}`;
    } else if (item.module === 'SERVICE_STAGE') {
      baseUrl = `/panel/production/edit/${item.entityId}`;
    } else if (item.module === 'TICKET') {
      baseUrl = `/panel/pipeline/ticket?ticketId=${item.entityId}`;
    } else if (item.module === 'ORDER') {
      baseUrl = `/panel/steel/edit/${item.entityId}`;
    } else if (item.module === 'USER') {
      baseUrl = `/panel/people?userId=${item.entityId}`;
    } else if (item.module === 'PRODUCT_APPROVAL' || item.module === 'CHECKLIST_ITEM' || item.module === 'BENEFICIAMENTO_NOTE') {
      baseUrl = `/panel/composition-product`;
    }

    if (item.module === 'USER' || baseUrl.startsWith('/panel/people')) {
      if (!baseUrl.includes('userId=')) {
        const separator = baseUrl.includes('?') ? '&' : '?';
        baseUrl = `${baseUrl}${separator}userId=${item.entityId}`;
      }
    }

    if (!baseUrl) return '';

    if (usageFile?.id) {
      const separator = baseUrl.includes('?') ? '&' : '?';
      return `${baseUrl}${separator}from=attachments&attachmentId=${usageFile.id}&returnTo=${encodeURIComponent(`/panel/attachments?attachmentId=${usageFile.id}`)}`;
    }

    return baseUrl;
  };

  const handleOpenDeleteConfirm = (file: GalleryFile) => {
    setActiveMenuFileId(null);
    setDeletingFile(file);
  };

  const handleConfirmDelete = async () => {
    if (!deletingFile) return;
    try {
      setIsDeleting(true);
      await AttachmentApiService.delete(deletingFile.id);
      setFiles((prev) => prev.filter((f) => f.id !== deletingFile.id));
      setTotalItems((prev) => Math.max(0, prev - 1));
      setFeedback({ isOpen: true, message: 'Anexo excluído com sucesso!', statusOk: true });
      setDeletingFile(null);
    } catch (error: any) {
      console.error(error);
      const apiMessage = error?.response?.data?.message;
      const errorMessage =
        typeof apiMessage === 'string'
          ? apiMessage
          : Array.isArray(apiMessage)
          ? apiMessage.join(', ')
          : error?.message || 'Erro ao excluir o anexo';

      const isAssociatedError =
        typeof errorMessage === 'string' &&
        errorMessage.toLowerCase().includes('associado a registros');

      setFeedback({
        isOpen: true,
        title: 'Atenção',
        message: isAssociatedError
          ? `${errorMessage} Clique em "Ver usos" no card para consultar os vínculos ativos.`
          : errorMessage,
        statusOk: false,
        timeout: 7000,
      });
      setDeletingFile(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const dropped = e.dataTransfer.files[0];
      setFileToUpload(dropped);
      if (!customTitle) {
        setCustomTitle(dropped.name);
      }
    }
  };

  const handleNativeFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selected = e.target.files[0];
      setFileToUpload(selected);
      if (!customTitle) {
        setCustomTitle(selected.name);
      }
    }
  };

  const handleUploadSubmit = async () => {
    if (!fileToUpload) {
      setFeedback({ isOpen: true, message: 'Selecione um arquivo para enviar', statusOk: false });
      return;
    }

    try {
      setUploading(true);
      const uploaded = await AttachmentApiService.upload(fileToUpload, customTitle);
      if (uploaded) {
        setFeedback({ isOpen: true, message: 'Anexo enviado com sucesso!', statusOk: true });
        setFileToUpload(null);
        setCustomTitle('');
        setUploadModalOpen(false);
        fetchGallery(1, false);
      }
    } catch (error) {
      console.error(error);
      setFeedback({ isOpen: true, message: 'Falha ao enviar arquivo', statusOk: false });
    } finally {
      setUploading(false);
    }
  };

  const columns: StandardColumn<GalleryFile>[] = useMemo(
    () => [
      {
        id: 'preview',
        label: 'Prévia',
        accessor: (file) => file.signedUrl || file.fileUrl,
        cell: (file) => {
          const badgeInfo = getFileBadgeInfo(
            file.mimeType,
            file.fileName || file.originalName || file.title || file.fileUrl
          );
          const isImg = badgeInfo.type === 'image';
          const previewUrl = file.signedUrl || file.fileUrl;
          const hasImageError = Boolean(failedImages[file.id]);

          if (isImg && !hasImageError) {
            return (
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 6,
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#f1f5f9',
                  cursor: 'pointer',
                  border: '1px solid #e2e8f0',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenLightbox(file.id);
                }}
                title="Clique para ampliar"
              >
                <img
                  src={previewUrl}
                  alt={file.title || file.originalName}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={() => setFailedImages((prev) => ({ ...prev, [file.id]: true }))}
                />
              </div>
            );
          }

          return (
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 6,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                cursor: 'pointer',
                color:
                  badgeInfo.type === 'pdf'
                    ? '#dc2626'
                    : badgeInfo.type === 'sheet'
                    ? '#16a34a'
                    : badgeInfo.type === 'doc'
                    ? '#2563eb'
                    : '#64748b',
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleViewFile(file);
              }}
              title="Clique para visualizar"
            >
              {React.cloneElement(badgeInfo.icon, { size: 18 })}
            </div>
          );
        },
      },
      {
        id: 'name',
        label: 'Nome do Arquivo',
        accessor: (file) => file.title || file.originalName || file.fileName || '-',
        cell: (file) => (
          <span style={{ fontWeight: 600, color: '#1e293b' }}>
            <OverflowCell text={file.title || file.originalName || file.fileName || '-'} maxWidth={1600} />
          </span>
        ),
        exportValue: (file) => file.title || file.originalName || file.fileName || '',
      },
      {
        id: 'type',
        label: 'Tipo',
        accessor: (file) =>
          getFileBadgeInfo(
            file.mimeType,
            file.fileName || file.originalName || file.title || file.fileUrl
          ).label,
        cell: (file) => {
          const badge = getFileBadgeInfo(
            file.mimeType,
            file.fileName || file.originalName || file.title || file.fileUrl
          );
          return <S.TypeBadge $type={badge.type}>{badge.label}</S.TypeBadge>;
        },
        exportValue: (file) =>
          getFileBadgeInfo(
            file.mimeType,
            file.fileName || file.originalName || file.title || file.fileUrl
          ).label,
      },
      {
        id: 'fileSize',
        label: 'Tamanho',
        accessor: (file) => file.fileSize || 0,
        cell: (file) => (
          <span style={{ color: '#64748b', fontSize: '0.82rem' }}>
            {formatSize(file.fileSize)}
          </span>
        ),
        exportValue: (file) => formatSize(file.fileSize),
      },
      {
        id: 'createdAt',
        label: 'Data de Envio',
        accessor: (file) => file.createdAt || '',
        cell: (file) => (
          <span style={{ color: '#64748b', fontSize: '0.82rem' }}>
            {formatDate(file.createdAt)}
          </span>
        ),
        exportValue: (file) => formatDate(file.createdAt),
      },
      {
        id: 'usage',
        label: 'Locais de Uso',
        accessor: (file) => (file.usageCount !== undefined ? (file.usageCount > 0 ? `Ver usos (${file.usageCount})` : 'Sem uso') : 'Ver usos'),
        cell: (file) => {
          const hasUsage = file.usageCount !== undefined ? file.usageCount > 0 : (file.hasUsage ?? true);
          return (
            <S.UsageBadgeButton
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenUsage(file);
              }}
              title={hasUsage ? 'Ver onde este anexo está sendo utilizado' : 'Este anexo não está em uso'}
              $hasUsage={hasUsage}
            >
              <Layers size={13} />
              <span>{file.usageCount !== undefined ? (hasUsage ? `Ver usos (${file.usageCount})` : 'Sem uso') : 'Ver usos'}</span>
            </S.UsageBadgeButton>
          );
        },
      },
    ],
    [failedImages, handleOpenLightbox, handleViewFile]
  );

  const { columnsState, handleColumnsChange, handleRestoreColumns } = useColumnsState(
    attachmentsScreenKey,
    columns
  );

  return (
    <S.Container>
      {loading && <LoadingComponent />}
      <PageHeader
        title="Anexos"
        tableName="GeneralFile"
        moduleName="Anexos"
        onReload={() => fetchGallery(1, false)}
        isLoading={loading}
        createButton={{
          label: 'Novo upload',
          onClick: () => {
            setFileToUpload(null);
            setCustomTitle('');
            setUploadModalOpen(true);
          },
        }}
        rightExtra={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <SortBySelector
              value={sortBy}
              onChange={(v) => setSortBy(v)}
              options={attachmentSortOptions}
            />
            <ViewModeSelector
              value={typeFilter}
              onChange={(val) => {
                setTypeFilter(val as any);
                setPage(1);
              }}
              options={attachmentTypeOptions}
            />
            <ViewModeSelector
              value={usageFilter}
              onChange={(val) => {
                setUsageFilter(val as any);
                setPage(1);
              }}
              options={attachmentUsageOptions}
            />
            <ViewModeSelector
              value={viewMode}
              onChange={(mode) => setViewMode(mode as 'board' | 'table')}
              boardLabel="Quadro"
              tableLabel="Tabela"
            />
          </div>
        }
      />

      <S.Content $isTable={viewMode === 'table'}>
        {files.length === 0 ? (
          <S.EmptyState>
            <FolderOpen size={48} />
            <h3>Nenhum anexo encontrado</h3>
            <p>
              {debouncedSearch
                ? `Nenhum anexo corresponde a "${debouncedSearch}".`
                : 'Não há arquivos cadastrados nesta categoria ainda.'}
            </p>
            <S.EmptyActionBtn onClick={() => setUploadModalOpen(true)}>
              <UploadCloud size={16} />
              Enviar primeiro anexo
            </S.EmptyActionBtn>
          </S.EmptyState>
        ) : viewMode === 'table' ? (
          <StandardDataTable
            tableName="GeneralFile"
            columns={columns}
            data={files}
            columnsState={columnsState}
            onColumnsChange={handleColumnsChange}
            onRestoreColumns={handleRestoreColumns}
            totalCount={totalItems}
            onLoadMore={handleLoadMore}
            hasMore={files.length < totalItems && page < totalPages}
            isLoading={loadingMore}
            countLabel="itens"
            exportFileName="anexos"
            emptyMessage="Nenhum anexo encontrado."
            rowActions={(file: GalleryFile) => [
              {
                label: 'Visualizar',
                icon: <Eye size={16} />,
                onClick: () => handleViewFile(file),
              },
              {
                label: 'Download',
                icon: <Download size={16} />,
                onClick: () => handleDownloadFile(file),
              },
              {
                label: 'Renomear',
                icon: <Edit2 size={16} />,
                onClick: () => handleOpenEdit(file),
              },
              {
                label: 'Ver locais de uso',
                icon: <Info size={16} />,
                onClick: () => handleOpenUsage(file),
              },
              {
                label: 'Excluir',
                icon: <Trash2 size={16} />,
                onClick: () => handleOpenDeleteConfirm(file),
              },
            ]}
            onRowClick={(file: GalleryFile) => handleViewFile(file)}
          />
        ) : (
          <>
            <S.FilesGrid>
              {files.map((file) => {
                const badgeInfo = getFileBadgeInfo(
                  file.mimeType,
                  file.fileName || file.originalName || file.title || file.fileUrl
                );
                const isImg = badgeInfo.type === 'image';
                const previewUrl = file.signedUrl || file.fileUrl;
                const hasImageError = Boolean(failedImages[file.id]);

                return (
                  <S.FileCard key={file.id}>
                    <S.CardTopBar>
                      <S.TypeBadge $type={badgeInfo.type}>
                        {badgeInfo.label}
                      </S.TypeBadge>

                      <div style={{ position: 'relative' }}>
                        <S.CardMenuButton
                          type="button"
                          title="Opções do anexo"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveMenuFileId((prev) => (prev === file.id ? null : file.id));
                          }}
                        >
                          ⋯
                        </S.CardMenuButton>

                        {activeMenuFileId === file.id && (
                          <S.CardMenuDropdown onClick={(e) => e.stopPropagation()}>
                            <S.CardMenuItem
                              type="button"
                              onClick={() => handleViewFile(file)}
                            >
                              <Eye size={14} />
                              Visualizar
                            </S.CardMenuItem>

                            <S.CardMenuItem
                              type="button"
                              onClick={() => handleDownloadFile(file)}
                            >
                              <Download size={14} />
                              Download
                            </S.CardMenuItem>

                            <S.CardMenuItem
                              type="button"
                              onClick={() => handleOpenEdit(file)}
                            >
                              <Edit2 size={14} />
                              Renomear
                            </S.CardMenuItem>

                            <S.CardMenuItem
                              type="button"
                              onClick={() => handleOpenUsage(file)}
                            >
                              <Info size={14} />
                              Ver locais de uso
                            </S.CardMenuItem>

                            <S.CardMenuItem
                              type="button"
                              $danger
                              onClick={() => handleOpenDeleteConfirm(file)}
                            >
                              <Trash2 size={14} />
                              Excluir
                            </S.CardMenuItem>
                          </S.CardMenuDropdown>
                        )}
                      </div>
                    </S.CardTopBar>

                    <S.ThumbnailWrapper
                      onClick={() => {
                        if (isImg && !hasImageError) {
                          handleOpenLightbox(file.id);
                        } else {
                          handleViewFile(file);
                        }
                      }}
                      title={isImg ? 'Clique para ampliar imagem' : 'Clique para visualizar arquivo'}
                    >
                      {isImg && !hasImageError ? (
                        <>
                          <img
                            src={previewUrl}
                            alt={file.title || file.originalName}
                            loading="lazy"
                            onError={() => {
                              setFailedImages((prev) => ({ ...prev, [file.id]: true }));
                            }}
                          />
                          <S.ZoomBadge>
                            <ZoomIn size={12} />
                          </S.ZoomBadge>
                        </>
                      ) : (
                        <S.FileIconPlaceholder $type={badgeInfo.type}>
                          {badgeInfo.icon}
                          <span>
                            {(
                              (file.fileName || file.originalName || file.title || file.fileUrl || '')
                                .split('?')[0]
                                .split('#')[0]
                                .split('.')
                                .pop() || 'ARQ'
                            )
                              .slice(0, 4)
                              .toUpperCase()}
                          </span>
                        </S.FileIconPlaceholder>
                      )}
                    </S.ThumbnailWrapper>

                    <S.CardBody>
                      <S.FileName title={file.title || file.originalName}>
                        {file.title || file.originalName}
                      </S.FileName>
                      <S.FileMetaRow>
                        <span>{formatSize(file.fileSize)}</span>
                        <span>{formatDate(file.createdAt)}</span>
                      </S.FileMetaRow>
                    </S.CardBody>

                    <S.CardFooter>
                      {(() => {
                        const hasUsage = file.usageCount !== undefined ? file.usageCount > 0 : (file.hasUsage ?? true);
                        return (
                          <S.UsageBadgeButton
                            type="button"
                            onClick={() => handleOpenUsage(file)}
                            title={hasUsage ? 'Ver onde este anexo está sendo utilizado' : 'Este anexo não está em uso'}
                            $hasUsage={hasUsage}
                          >
                            <Layers size={13} />
                            <span>{file.usageCount !== undefined ? (hasUsage ? `Ver usos (${file.usageCount})` : 'Sem uso') : 'Ver usos'}</span>
                          </S.UsageBadgeButton>
                        );
                      })()}

                      <button
                        type="button"
                        onClick={() => handleDownloadFile(file)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#64748b',
                          padding: '2px',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                        title="Baixar anexo"
                      >
                        <Download size={14} />
                      </button>
                    </S.CardFooter>
                  </S.FileCard>
                );
              })}
            </S.FilesGrid>

            {files.length > 0 && (
              <S.LoadMoreWrapper>
                <LoadMoreButton
                  currentCount={files.length}
                  totalCount={totalItems}
                  onLoadMore={handleLoadMore}
                  isLoading={loadingMore}
                  hasMore={files.length < totalItems && page < totalPages}
                  label="itens"
                />
              </S.LoadMoreWrapper>
            )}
          </>
        )}
      </S.Content>

      {uploadModalOpen && (
        <S.ModalOverlay onClick={() => setUploadModalOpen(false)}>
          <S.ModalContainer onClick={(e) => e.stopPropagation()}>
            <S.ModalHeader>
              <S.ModalTitle>
                <UploadCloud size={18} color="#0090D3" />
                Upload de Novo Anexo
              </S.ModalTitle>
              <S.CloseButton onClick={() => setUploadModalOpen(false)}>
                <X size={18} />
              </S.CloseButton>
            </S.ModalHeader>

            <S.ModalBody>
              <S.DropzoneArea
                $isDragOver={isDragOver}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragOver(true);
                }}
                onDragLeave={() => setIsDragOver(false)}
                onDrop={handleFileDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <S.DropzoneIcon>
                  <UploadCloud size={24} />
                </S.DropzoneIcon>
                <div>
                  <h4 style={{ margin: 0, fontSize: '0.9rem', color: '#1e293b' }}>
                    {fileToUpload ? fileToUpload.name : 'Arraste e solte o arquivo aqui'}
                  </h4>
                  <p style={{ margin: '4px 0 0 0', fontSize: '0.78rem', color: '#64748b' }}>
                    {fileToUpload
                      ? `${formatSize(fileToUpload.size)} - Clique para trocar`
                      : 'ou clique para selecionar do computador'}
                  </p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  style={{ display: 'none' }}
                  onChange={handleNativeFileChange}
                />
              </S.DropzoneArea>

              <S.InputGroup>
                <S.InputLabel>Título / Descrição (opcional)</S.InputLabel>
                <S.StyledInput
                  type="text"
                  className="inline-input"
                  placeholder="Ex: Ficha de especificações, Contrato, Foto..."
                  value={customTitle}
                  onChange={(e) => setCustomTitle(e.target.value)}
                />
              </S.InputGroup>
            </S.ModalBody>

            <S.ModalFooter>
              <S.CancelButton type="button" onClick={() => setUploadModalOpen(false)}>
                Cancelar
              </S.CancelButton>
              <S.PrimaryButton
                type="button"
                disabled={!fileToUpload || uploading}
                onClick={handleUploadSubmit}
              >
                {uploading ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Enviando...
                  </>
                ) : (
                  'Fazer Upload'
                )}
              </S.PrimaryButton>
            </S.ModalFooter>
          </S.ModalContainer>
        </S.ModalOverlay>
      )}

      {editingFile && (
        <S.ModalOverlay onClick={() => setEditingFile(null)}>
          <S.ModalContainer onClick={(e) => e.stopPropagation()}>
            <S.ModalHeader>
              <S.ModalTitle>
                <Edit2 size={18} color="#0090D3" />
                Renomear Anexo
              </S.ModalTitle>
              <S.CloseButton onClick={() => setEditingFile(null)}>
                <X size={18} />
              </S.CloseButton>
            </S.ModalHeader>

            <S.ModalBody>
              <S.InputGroup>
                <S.InputLabel>Novo nome do arquivo</S.InputLabel>
                <S.StyledInput
                  type="text"
                  className="inline-input"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveEdit();
                    if (e.key === 'Escape') setEditingFile(null);
                  }}
                />
              </S.InputGroup>
            </S.ModalBody>

            <S.ModalFooter>
              <S.CancelButton type="button" onClick={() => setEditingFile(null)}>
                Cancelar
              </S.CancelButton>
              <S.PrimaryButton
                type="button"
                disabled={savingEdit || !editName.trim()}
                onClick={handleSaveEdit}
              >
                {savingEdit ? 'Salvando...' : 'Salvar'}
              </S.PrimaryButton>
            </S.ModalFooter>
          </S.ModalContainer>
        </S.ModalOverlay>
      )}

      {deletingFile && (
        <S.ModalOverlay onClick={() => setDeletingFile(null)}>
          <S.ModalContainer onClick={(e) => e.stopPropagation()}>
            <S.ModalHeader>
              <S.ModalTitle style={{ color: '#dc2626' }}>
                <Trash2 size={18} color="#dc2626" />
                Confirmar Exclusão
              </S.ModalTitle>
              <S.CloseButton onClick={() => setDeletingFile(null)}>
                <X size={18} />
              </S.CloseButton>
            </S.ModalHeader>

            <S.ModalBody>
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#334155', lineHeight: 1.5 }}>
                Tem certeza que deseja excluir o anexo{' '}
                <strong>&quot;{deletingFile.title || deletingFile.originalName}&quot;</strong>?
              </p>
              <p style={{ margin: 0, fontSize: '0.78rem', color: '#94a3b8' }}>
                Esta ação removerá o arquivo físico do bucket e seu cadastro no sistema.
              </p>
            </S.ModalBody>

            <S.ModalFooter>
              <S.CancelButton type="button" onClick={() => setDeletingFile(null)}>
                Cancelar
              </S.CancelButton>
              <S.PrimaryButton
                type="button"
                $danger
                disabled={isDeleting}
                onClick={handleConfirmDelete}
              >
                {isDeleting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Excluindo...
                  </>
                ) : (
                  'Excluir Definitivamente'
                )}
              </S.PrimaryButton>
            </S.ModalFooter>
          </S.ModalContainer>
        </S.ModalOverlay>
      )}

      {usageFile && (
        <S.ModalOverlay onClick={handleCloseUsage}>
          <S.ModalContainer onClick={(e) => e.stopPropagation()}>
            <S.ModalHeader>
              <S.ModalTitle>
                <Layers size={18} color="#0090D3" />
                Locais de Uso do Anexo
              </S.ModalTitle>
              <S.CloseButton onClick={handleCloseUsage}>
                <X size={18} />
              </S.CloseButton>
            </S.ModalHeader>

            <S.ModalBody>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingBottom: 8, borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>
                  {usageFile.title || usageFile.originalName}
                </span>
              </div>

              {loadingUsage ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '30px 0', gap: 8, color: '#64748b' }}>
                  <Loader2 size={20} className="animate-spin" />
                  <span style={{ fontSize: '0.85rem' }}>Consultando vínculos...</span>
                </div>
              ) : usageList.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '30px 0', color: '#94a3b8' }}>
                  <FolderOpen size={36} style={{ margin: '0 auto 8px auto', display: 'block', color: '#cbd5e1' }} />
                  <p style={{ margin: 0, fontWeight: 600, color: '#475569', fontSize: '0.9rem' }}>Nenhum uso encontrado</p>
                  <p style={{ margin: '4px 0 0 0', fontSize: '0.78rem' }}>Este anexo não está vinculado a cards, produtos ou pedidos.</p>
                </div>
              ) : (
                usageList.map((item, idx) => {
                  const editUrl = getUsageEditUrl(item);
                  return (
                    <S.UsageItem key={`${item.module}-${item.entityId}-${idx}`}>
                      <S.UsageItemLeft>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <S.UsageBadge>{getModuleBadgeLabel(item)}</S.UsageBadge>
                          {item.isCover && (
                            <span style={{ background: '#fef3c7', color: '#b45309', fontSize: '0.65rem', fontWeight: 700, padding: '2px 6px', borderRadius: 4 }}>
                              Capa
                            </span>
                          )}
                        </div>
                        {editUrl ? (
                          <S.UsageRecordLink
                            href={editUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => {
                              if (!e.ctrlKey && !e.metaKey && e.button !== 1) {
                                e.preventDefault();
                                navigate(editUrl);
                                setUsageFile(null);
                              }
                            }}
                          >
                            {item.recordName}
                          </S.UsageRecordLink>
                        ) : (
                          <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#334155' }}>
                            {item.recordName}
                          </span>
                        )}
                      </S.UsageItemLeft>

                      {editUrl && (
                        <S.UsageEditButton
                          href={editUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => {
                            if (!e.ctrlKey && !e.metaKey && e.button !== 1) {
                              e.preventDefault();
                              navigate(editUrl);
                              setUsageFile(null);
                            }
                          }}
                        >
                          <Edit2 size={12} />
                          Abrir
                        </S.UsageEditButton>
                      )}
                    </S.UsageItem>
                  );
                })
              )}
            </S.ModalBody>

            <S.ModalFooter>
              <S.CancelButton type="button" onClick={handleCloseUsage}>
                Fechar
              </S.CancelButton>
            </S.ModalFooter>
          </S.ModalContainer>
        </S.ModalOverlay>
      )}

      <ImageLightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        slides={lightboxSlides}
        index={lightboxIndex}
      />
    </S.Container>
  );
};

export default AttachmentsPage;
